import {Injectable} from '@angular/core';
import {TRIGGER_ACTION_TYPE} from '@constants/trigger/trigger-action-type.constant';
import {Trigger} from '@models/adminstration/machine/trigger/trigger';
import {Role} from '@models/user/role';
import {TRIGGER_SIGNAL_TYPE} from '@constants/administration/machine/trigger-signal-label';
import {
  AddTriggerConfigurationRequest,
  TriggerConfiguration
} from '@models/adminstration/machine/trigger/trigger-configuration';
import {SignalEdge} from '@models/adminstration/machine/trigger/signal-edge';
import {
  ArithmeticOperator,
  TriggerCorrelation,
  TriggerExpression
} from '@models/adminstration/machine/trigger/trigger-expression';
import {TriggerExpressionTypes} from '@constants/administration/machine/trigger-expression-type';
import {TRIGGER_TYPE} from '@constants/administration/machine/trigger-type';

@Injectable({
  providedIn: 'root'
})
export class TriggerHelperService {

  public getBadgeClass(triggerActionType: TRIGGER_ACTION_TYPE): string {
    switch (triggerActionType) {

      case TRIGGER_ACTION_TYPE.EMAIL:
        return 'badge-success';
      case TRIGGER_ACTION_TYPE.NOTIFICATION:
        return 'badge-primary';
      case TRIGGER_ACTION_TYPE.PUBSUB:
        return 'badge-danger';
      default:
        return '';
    }
  }

  public setDeleteButtonIsEnabled(trigger: Trigger, userRole: Role): boolean {
    if (trigger.usedInActiveTrigger) {
      return false;
    }
    if (userRole.isAdminRole && (
        trigger.triggerActionType === TRIGGER_ACTION_TYPE.EMAIL ||
        trigger.triggerActionType === TRIGGER_ACTION_TYPE.NOTIFICATION)) {
      return true;
    }
    if (userRole.isSuperAdminRole || userRole.isTechAdminRole) {
      return true;
    }
    if (trigger.triggerActionType === TRIGGER_ACTION_TYPE.PUBSUB) {
      return false;
    }
    return userRole.isAdminRole && trigger.triggerActionType === TRIGGER_ACTION_TYPE.EMAIL;
  }

  public parseValue(event: TRIGGER_SIGNAL_TYPE): string[] {
    switch (event) {
      case TRIGGER_SIGNAL_TYPE.ERROR:
        return ['0', '5'];
      case TRIGGER_SIGNAL_TYPE.WARNING:
        return ['1', '6'];
      case TRIGGER_SIGNAL_TYPE.INHIBIT:
        return ['2', '7'];
      case TRIGGER_SIGNAL_TYPE.INFO:
        return ['3', '8'];
      case TRIGGER_SIGNAL_TYPE.DEBUG:
        return ['4', '9'];
    }
  }

  public getEventLabelByArray(severitiesArray: string[]): TRIGGER_SIGNAL_TYPE[] | null {
    const result: TRIGGER_SIGNAL_TYPE[] = [];
    for (const severity of severitiesArray) {
      let type: TRIGGER_SIGNAL_TYPE = TRIGGER_SIGNAL_TYPE.ERROR;
      switch (severity) {
        case '0':
        case '5': {
          type = TRIGGER_SIGNAL_TYPE.ERROR;
          break;
        }
        case '1':
        case '6': {
          type = TRIGGER_SIGNAL_TYPE.WARNING;
          break;
        }
        case '2':
        case '7': {
          type = TRIGGER_SIGNAL_TYPE.INHIBIT;
          break;
        }
        case '3':
        case '8': {
          type = TRIGGER_SIGNAL_TYPE.INFO;
          break;
        }
        case '4':
        case '9': {
          type = TRIGGER_SIGNAL_TYPE.DEBUG;
          break;
        }
      }

      if (result.indexOf(type) < 0) {
        result.push(type);
      }
    }
    return result;
  }

  public getEventLabelByNumber(num: number): TRIGGER_SIGNAL_TYPE | null {
    if (num === 0) {
      return TRIGGER_SIGNAL_TYPE.ERROR;
    }
    if (num === 1) {
      return TRIGGER_SIGNAL_TYPE.WARNING;
    }
    if (num === 2) {
      return TRIGGER_SIGNAL_TYPE.INHIBIT;
    }
    if (num === 3) {
      return TRIGGER_SIGNAL_TYPE.INFO;
    }
    if (num === 4) {
      return TRIGGER_SIGNAL_TYPE.DEBUG;
    }
    return null;
  }

  public setEventSeverityRequest(
      triggerConfiguration: TriggerConfiguration,
      triggerType: TRIGGER_TYPE,
      severities: TRIGGER_SIGNAL_TYPE[],
      name: string
  ): AddTriggerConfigurationRequest {
    let request: AddTriggerConfigurationRequest;
    if (triggerConfiguration.id > 0) {
      request = new TriggerConfiguration(triggerConfiguration);
    } else {
      request = new AddTriggerConfigurationRequest(triggerConfiguration);
      request.signalEdge = SignalEdge.RISING_EDGE;
    }
    request.triggerExpressions = this.setEventSeverityTriggerExpressions(severities, 'EventSeverity');
    request.triggerType = triggerType;
    request.name = name;
    request.triggerQueries = triggerConfiguration.triggerQueries.map(({id}) => id);

    return request;
  }

  public setValueBasedRequest(triggerConfiguration: TriggerConfiguration, triggerType: TRIGGER_TYPE, name: string): AddTriggerConfigurationRequest {
    let request: AddTriggerConfigurationRequest | TriggerConfiguration;
    if (triggerConfiguration.id > 0) {
      request = new TriggerConfiguration(triggerConfiguration);
      request.name = name;
      request.triggerType = triggerType;
    } else {
      request = new AddTriggerConfigurationRequest(triggerConfiguration);
      request.name = name;
      request.triggerType = triggerType;
    }
    request.triggerQueries = triggerConfiguration.triggerQueries.map(({id}) => id);
    return request;
  }

  private setEventSeverityTriggerExpressions(severities: TRIGGER_SIGNAL_TYPE[], triggerExpressionType: string): TriggerExpression[] {
    const expressions: TriggerExpression[] = [];

    for (const severity of severities) {
      expressions.push(new TriggerExpression(ArithmeticOperator.EQUAL, triggerExpressionType, '', triggerExpressionType, TriggerCorrelation.OR,
          TriggerExpressionTypes.CONDITION, this.parseValue(severity)[0], '', '', null));
      expressions.push(new TriggerExpression(ArithmeticOperator.EQUAL, triggerExpressionType, '', triggerExpressionType, TriggerCorrelation.OR,
          TriggerExpressionTypes.CONDITION, this.parseValue(severity)[1], '', '', null));
    }
    return expressions;
  }
}
